import { Injectable } from '@angular/core';
import {
    FlyFreelyError,
    FlyFreelyLoggingService,
    PersonRolesDto,
    PersonService,
    PersonWithRoles,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import {
    NgSelectPaginationValues,
    NgSelectSearch,
    ngSelectPreSearchConfigure,
    updateNgSelectData
} from '@flyfreely-portal-ui/ng-select';
import { personSearch } from '@flyfreely-portal-ui/ui';
import { BehaviorSubject, Subject, debounceTime, takeUntil } from 'rxjs';

@Injectable()
export class MissionCompletionPersonnelSelectDataService {
    organisationId: number;
    excludedIds: number[];
    roleFilter: PersonRolesDto.Roles;

    personSearch = personSearch.bind(this);

    private personnelSource = new BehaviorSubject<PersonWithRoles[]>([]);
    personnel$ = this.personnelSource.asObservable();

    private personSearchSource = new Subject<NgSelectSearch>();
    personSelectConfig: NgSelectPaginationValues = {
        currentPage: 0,
        preSearchPage: null,
        searchValue: null,
        totalItems: null
    };

    private workTracker = new WorkTracker();
    private workingSource = new BehaviorSubject<boolean>(false);
    working$ = this.workingSource.asObservable();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private personService: PersonService,
        private logging: FlyFreelyLoggingService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => this.workingSource.next(working));

        this.personSearchSource
            .pipe(debounceTime(300), takeUntil(this.ngUnsubscribe$))
            .subscribe(search => {
                this.personSelectConfig = ngSelectPreSearchConfigure(
                    search,
                    this.personSelectConfig
                );
                this.refreshPersonList();
            });
    }

    ngOnDestroy() {
        this.personSearchSource.complete();
        this.personnelSource.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    setParameters(
        organisationId: number,
        role: PersonRolesDto.Roles,
        excludeIds: number[]
    ) {
        this.organisationId = organisationId;
        this.roleFilter = role;
        this.excludedIds = excludeIds;
        this.resetPersonSelectConfig();
    }

    private refreshPersonList() {
        this.personService
            .findPersonnelGraphQL(
                this.organisationId,
                this.personSelectConfig.currentPage,
                15,
                {
                    roleOrList: [this.roleFilter],
                    textSearch:
                        this.personSelectConfig.searchValue || undefined,
                    excludedIdList: this.excludedIds
                }
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    const newPersonnel = result.results.filter(
                        p =>
                            this.personnel.findIndex(
                                person => person.id === p.id
                            ) === -1
                    );
                    const updatedPersonnel =
                        this.personnel.concat(newPersonnel);

                    this.personnelSource.next(updatedPersonnel);

                    this.personSelectConfig = updateNgSelectData(
                        result,
                        this.personSelectConfig
                    );
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading mission personnel: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    onPersonSelectScroll() {
        const resultLength =
            this.personSelectConfig.searchValue == null
                ? this.personnel.length
                : this.personnel.filter(person =>
                      this.personSearch(
                          this.personSelectConfig.searchValue,
                          person
                      )
                  ).length;

        if (
            this.personSelectConfig.totalItems == null ||
            resultLength !== this.personSelectConfig.totalItems
        ) {
            this.refreshPersonList();
        }
    }

    onPersonSelectSearch(search: NgSelectSearch) {
        this.personSearchSource.next(search);
    }

    onPersonSelectClose() {
        this.resetPersonSelectConfig();
    }

    private resetPersonSelectConfig() {
        this.personSelectConfig = {
            ...this.personSelectConfig,
            searchValue: null,
            currentPage: 0,
            preSearchPage: null
        };
        this.refreshPersonList();
    }

    private get personnel() {
        return this.personnelSource.getValue();
    }
}
